"use client";

import Link from "next/link";
import { capture } from "@/lib/analytics/events";

/**
 * Apply button on a job detail page, plus the portfolio link that sits under
 * it BEFORE anyone has applied.
 *
 * The click also dispatches a `finseats:apply_click` window event, which is
 * what reveals PostApplyPanel — the offer shown when the visitor comes back
 * from the employer's ATS.
 *
 * History worth keeping: this component used to reframe its own link once
 * `apply_click` fired ("Application opened — track it in your portfolio →").
 * That reframe was measured and did not work: 5 clicks against 417
 * apply_clicks over 30 days, none of them on mobile, and `portfolio_cta_seen`
 * proved the box was being seen. So the post-apply moment now belongs to
 * PostApplyPanel, which pitches the video application for the job they just
 * applied to with the price on the same screen (T-246), and the link here
 * simply steps aside once applied rather than competing with it.
 */
export function ApplyPortfolioCta({
  href,
  jobId,
  orgKey,
  placement,
  applyClassName,
  ctaLocation,
  ctaClassName,
  children,
}: {
  href: string;
  jobId: number;
  orgKey: string;
  placement: "sidebar" | "mobile";
  applyClassName: string;
  ctaLocation: "job_detail_sidebar" | "job_detail_mobile";
  ctaClassName: string;
  children?: React.ReactNode;
}) {
  return (
    <>
      <a
        href={href}
        target="_blank"
        rel="noopener nofollow"
        onClick={() => {
          capture("apply_click", { job_id: jobId, org_key: orgKey, placement });
          window.dispatchEvent(
            new CustomEvent("finseats:apply_click", { detail: { jobId, orgKey } }),
          );
        }}
        className={applyClassName}
      >
        Apply on official site ↗
      </a>
      {children}
      <Link
        href="/dashboard"
        onClick={() => capture("portfolio_cta_click", { location: ctaLocation })}
        className={ctaClassName}
      >
        Build a portfolio to stand out
      </Link>
    </>
  );
}
